import Styles from './styles.module.css';
import Styless from '../../styles.module.css';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { deleteField, doc, getDoc, getFirestore, onSnapshot, setDoc, writeBatch } from 'firebase/firestore';
import { isSupportedJobPostingYear, jobPostingYearDoc } from '../jobPostingRef';
import { unsavedGuard } from '../../unsavedGuard';
import Navigation from "./navigation/index";
import Info from "./2.edit/2.info";
import Employment from "./2.edit/5.20-employment";
import Employment18 from "./2.edit/6.18-employment";
import Employment17 from "./2.edit/7.17-employment";
import Benefit from "./2.edit/8-benefit";
import Target from "./2.edit/4.target";
import ApplicationInfo from "./2.edit/9.applicationInfo";
import Pamphlet from "../../1.manual/3.school/2.edit/10.pamphlet";
import BackCaution from "../../1.manual/3.school/backCaution/index";
import Preview from "./preview/index";
import Summary from '../../1.manual/3.school/2.edit/1.summary';
import Stores from '../../3.recruit/2.edit/2.store';
import Confirm from './confirm/index';
import { defaultSummaryData, defaultInfo, defaultApplicationInfo, defaultPamphlet, formatSummary, formatInfo, formatApplicationInfo, formatPamphlet, defaultBenefit, defaultEmployment20, defaultEmployment18, defaultEmployment17, defaultTarget, formatTarget, formatBenefit, formatEmployment20, formatEmployment18, formatEmployment17, defaultHashTagsData, formatHashTags } from "../../../../type";
import { useAppSelector } from 'src/salon/app/hook';
import HashTag from './2.edit/11.hashTag';
import { PiCheckCircleFill } from "react-icons/pi";
import { VscChevronRight, VscChevronLeft } from "react-icons/vsc";

// 求人票(年度ごとに学校へ送付する書類)の編集画面。
//
// 会社概要・PR(summary)、店舗情報の完了フラグ(store.isCompleted)、パンフレット
// (pamphlet)は「求人編集(3.recruit、アプリ上に公開する求人ページ)」と共有の
// データのため、これまで通り recruits/{companyId}_2027 のフラットドキュメントを
// 読み書きする(1.manual/3.school・3.recruit の該当コンポーネントをそのまま再利用)。
//
// 一方、企業情報(info)・募集区分(target)・雇用条件(employment20/18/17)・
// 福利厚生(benefit)・応募情報(applicationInfo)・ハッシュタグ(hashTag)は
// 年度ごとに内容が変わる求人票専有のデータのため、
// recruits/{companyId}_2027/jobPostings/{year} のサブコレクションドキュメントに
// 保存する(親は既存のフラットドキュメント {companyId}_2027 そのもの。パスの
// 組み立ては ../jobPostingRef に集約。2.edit/ 以下は 1.manual/3.school の同名
// コンポーネントを複製し、保存先だけをこのサブコレクションに向けたもの)。
//
// 1.manual/3.school(オンボーディングの「求人票を作成しましょう」)は
// このファイルから一切参照されておらず、変更の影響を受けない。

const MOBILE_NAV_ITEMS = [
  { id: 1, label: "魅力" },
  { id: 2, label: "企業情報" },
  { id: 3, label: "店舗情報" },
  { id: 4, label: "募集区分" },
  { id: 5, label: "雇用条件(20歳卒)" },
  { id: 6, label: "雇用条件(18歳卒)", targetCondition: "高専(18歳卒)" },
  { id: 7, label: "雇用条件(17歳卒)", targetCondition: "高専(17歳卒)" },
  { id: 8, label: "福利厚生" },
  { id: 9, label: "採用情報" },
  { id: 10, label: "パンフレット" },
  { id: 11, label: "ハッシュタグ" },
];

// 年度間コピー(「{前年度}卒の求人をコピーする」ボタン)でコピーする対象フィールド。
// jobPostingsStatus(公開フラグ)はコピー対象外(コピー先の公開状態を勝手に変えない)。
const COPY_YEAR_FIELDS = [
  'info',
  'target',
  'employment20',
  'employment18',
  'employment17',
  'benefit',
  'applicationInfo',
  'hashTag',
  'warning',
] as const;

function checkProgressMobile(progress: boolean[], target: string[]) {
  return progress.slice(0, 11).every((item, index) => {
    if (!target?.includes('高専(18歳卒)') && index === 5) return true;
    if (!target?.includes('高専(17歳卒)') && index === 6) return true;
    return Boolean(item);
  });
}

const JobPostingEdit: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);
  const db = getFirestore();

  const { year: yearParam } = useParams<{ year: string }>();
  // 許可されていない年度(URL直打ちの /service/jobPostings/2099 等)は空文字にして、
  // 年度ドキュメントの作成・読み込みをさせず、下の return で一覧へ戻す。
  const yearSupported = isSupportedJobPostingYear(yearParam);
  const year = yearSupported ? yearParam : '';
  const companyId = company?.id ?? '';

  const savedData = useRef<any>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const [documentId, setDocumentId] = useState<string>("");
  const [navigation, setNavigation] = useState<number>(1);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const [progress, setProgress] = useState<boolean[]>(Array(11).fill(false));
  const [changeData, setChangeData] = useState<boolean>(false);
  const [backCaution, setBackCaution] = useState<number>(0);

  const [summary, setSummary] = useState(defaultSummaryData);
  const memoizedSummary = useMemo(() => summary, [summary]);

  const [info, setInfo] = useState(defaultInfo);
  const memoizedInfo = useMemo(() => info, [info]);

  const [target, setTarget] = useState(defaultTarget);
  const memoizedTarget = useMemo(() => target, [target]);

  const [benefit, setBenefit] = useState(defaultBenefit);
  const memoizedBenefit = useMemo(() => benefit, [benefit]);

  const [employment20, setEmployment20] = useState(defaultEmployment20);
  const memoizedEmployment20 = useMemo(() => employment20, [employment20]);

  const [employment18, setEmployment18] = useState(defaultEmployment18);
  const memoizedEmployment18 = useMemo(() => employment18, [employment18]);

  const [employment17, setEmployment17] = useState(defaultEmployment17);
  const memoizedEmployment17 = useMemo(() => employment17, [employment17]);

  const [applicationInfo, setApplicationInfo] = useState(defaultApplicationInfo);
  const memoizedApplicationInfo = useMemo(() => applicationInfo, [applicationInfo]);

  const [pamphlet, setPamphlet] = useState(defaultPamphlet);
  const memoizedPamphlet = useMemo(() => pamphlet, [pamphlet]);

  const [hashTag, setHashTag] = useState(defaultHashTagsData);
  const memoizedHashTag = useMemo(() => hashTag, [hashTag]);

  const [storeImageLocal, setStoreImageLocal] = useState<string>("");

  const [confirm, setConfirm] = useState<boolean>(false);

  const [oldData, setOldData] = useState<any>(null);
  const [newData, setNewData] = useState<any>(null);

  const [copyingPreviousYear, setCopyingPreviousYear] = useState<boolean>(false);

  const navigate = useNavigate();

  // 前年度(卒業年度が1つ前)の求人票の内容をこの年度にコピーする。
  // 対象は求人票専有フィールド(COPY_YEAR_FIELDS)のみで、公開フラグ
  // (jobPostingsStatus)はコピーしない(コピー先の公開状態はそのまま)。
  const previousYear = /^\d+$/.test(year) ? String(Number(year) - 1) : '';

  const handleCopyPreviousYear = async () => {
    if (!companyId || !previousYear || copyingPreviousYear) return;

    if (!window.confirm(`${previousYear}卒の求人票の内容をこの年度(${year}卒)にコピーします。現在入力中の内容は上書きされます。よろしいですか？`)) {
      return;
    }

    setCopyingPreviousYear(true);

    try {
      const prevSnap = await getDoc(jobPostingYearDoc(db, companyId, previousYear));

      if (!prevSnap.exists()) {
        alert(`${previousYear}卒の求人票データが見つかりませんでした。`);
        return;
      }

      const prevData = prevSnap.data() ?? {};
      const payload: Record<string, unknown> = {};

      // 項目ごとに丸ごと置き換え、コピー元に無い項目はコピー先からも消す。
      // (以前は setDoc の merge:true で書いていたため、コピー元に無い項目や、
      //  employment20 等の中でコピー元に無いキーにコピー先の古い値が残り、年度が混ざっていた)
      COPY_YEAR_FIELDS.forEach((field) => {
        payload[field] = field in prevData
          ? (prevData as Record<string, unknown>)[field]
          : deleteField();
      });

      const targetRef = jobPostingYearDoc(db, companyId, year);
      const batch = writeBatch(db);
      batch.set(targetRef, {}, { merge: true }); // コピー先ドキュメントの存在を保証
      batch.update(targetRef, payload as Record<string, any>);
      await batch.commit();
    } catch (error) {
      console.error('前年度からのコピーに失敗しました:', error);
      alert('コピーに失敗しました。');
    } finally {
      setCopyingPreviousYear(false);
    }
  };

  // 未保存の編集があるまま、この画面から別画面へ移動しようとしたときに確認する。
  //   - 対象: 別タブ(サロンPR等)への移動 / 「求人票の一覧に戻る」ボタン
  //           (サイドバーのクリックは salon/desktop/index.tsx 側で unsavedGuard を経由)
  //   - 対象外: 11セクション間の切り替え(従来どおり backCaution が担当)
  //   - 確定後(confirm)は未保存編集が無いので素通し
  useEffect(() => {
    unsavedGuard.setDirty(changeData && !confirm);
    return () => unsavedGuard.setDirty(false);
  }, [changeData, confirm]);

  // ブラウザの「閉じる / リロード」にもネイティブ警告を出す
  // (文言はブラウザ既定。任意テキストは指定できない)
  useEffect(() => {
    if (!changeData) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [changeData]);

  // -------------------------
  // Firestore同期 (1): summary / store / pamphlet ← recruits/{companyId}_2027 (3.recruitと共有)
  // -------------------------
  useEffect(() => {
    if (!company?.id) return;

    const oldId = company.id + '_2027';
    setDocumentId(oldId);

    const oldRef = doc(db, "recruits", oldId);

    const unsubscribe = onSnapshot(oldRef, (docSnap) => {
      setOldData(docSnap.exists() ? docSnap.data() : {});
    });

    return () => unsubscribe();
  }, [company.id]);

  // -------------------------
  // Firestore同期 (2): info / target / employment* / benefit / applicationInfo / hashTag
  //                     ← recruits/{companyId}_2027/jobPostings/{year} (年度別・求人票専有)
  // -------------------------
  useEffect(() => {
    if (!companyId || !year) return;

    const newRef = jobPostingYearDoc(db, companyId, year);

    // まだドキュメントが存在しない場合(バックフィル未実施・新規会社等)に備えて
    // 空ドキュメントを用意しておく。merge:true なので既存データは壊さない。
    setDoc(newRef, {}, { merge: true }).catch((error) => {
      console.error('求人票データの初期化に失敗しました:', error);
    });

    const unsubscribe = onSnapshot(newRef, (docSnap) => {
      setNewData(docSnap.exists() ? docSnap.data() : {});
    });

    return () => unsubscribe();
  }, [companyId, year]);

  // -------------------------
  // (1)(2)の同期結果を合成してフォームの状態に反映
  // -------------------------
  useEffect(() => {
    if (oldData === null || newData === null) return;

    const statusKeys = [
      oldData.summary?.isCompleted,
      newData.info?.isCompleted,
      oldData.store?.isCompleted,
      newData.target?.isCompleted,
      newData.employment20?.isCompleted,
      newData.employment18?.isCompleted,
      newData.employment17?.isCompleted,
      newData.benefit?.isCompleted,
      newData.applicationInfo?.isCompleted,
      oldData.pamphlet?.isCompleted,
      newData.hashTag?.isCompleted,
    ];

    setProgress(statusKeys);

    const formatted = {
      summary: formatSummary(oldData),
      pamphlet: formatPamphlet(oldData),
      info: formatInfo(newData),
      target: formatTarget(newData),
      employment20: formatEmployment20(newData),
      employment18: formatEmployment18(newData),
      employment17: formatEmployment17(newData),
      applicationInfo: formatApplicationInfo(newData),
      benefit: formatBenefit(newData),
      hashTag: formatHashTags(newData),
    };

    savedData.current = formatted;

    setSummary(formatted.summary);
    setInfo(formatted.info);
    setTarget(formatted.target);
    setEmployment20(formatted.employment20);
    setEmployment18(formatted.employment18);
    setEmployment17(formatted.employment17);
    setApplicationInfo(formatted.applicationInfo);
    setBenefit(formatted.benefit);
    setPamphlet(formatted.pamphlet);
    setHashTag(formatted.hashTag);
  }, [oldData, newData]);

  const resetToSaved = () => {
    if (!savedData.current) return;
    const d = savedData.current;
    setSummary(d.summary);
    setInfo(d.info);
    setTarget(d.target);
    setEmployment20(d.employment20);
    setEmployment18(d.employment18);
    setEmployment17(d.employment17);
    setApplicationInfo(d.applicationInfo);
    setBenefit(d.benefit);
    setPamphlet(d.pamphlet);
    setHashTag(d.hashTag);
  };

  const handleMobilePublish = () => {
    const buttons = navRef.current?.querySelectorAll('button');
    if (buttons && buttons.length > 0) {
      const confirmBtn = buttons[buttons.length - 1] as HTMLButtonElement;
      confirmBtn.click();
    }
    setMobileNavOpen(false);
  };


  if (!yearSupported) {
    return <Navigate to="/service/jobPostings" replace />;
  }

  return (
    <>
    <div className={Styless.works_service}>
    <div>
    {confirm ?
      <Confirm companyId={companyId} year={year} target={target} setConfirm={setConfirm} />
    :
      <>
      <div className={Styles.topBar}>
        <button
          type="button"
          className={Styles.backToList}
          onClick={() => unsavedGuard.request(() => navigate('/service/jobPostings'))}
        >
          <VscChevronLeft className={Styles.backToList_icon} />求人票の一覧に戻る
        </button>

        {previousYear && (
          <button
            type="button"
            className={Styles.copyPreviousYear}
            onClick={handleCopyPreviousYear}
            disabled={copyingPreviousYear}
          >
            {copyingPreviousYear ? 'コピー中...' : `${previousYear}卒の求人をコピーする`}
          </button>
        )}
      </div>

      <div className={Styles.box}>

        {/* デスクトップ用ナビゲーション（モバイルでは画面外に退避） */}
        <div ref={navRef} className={Styles.desktop_nav}>
          <Navigation
            summary={memoizedSummary}
            info={memoizedInfo}
            target={memoizedTarget}
            benefit={memoizedBenefit}
            employment20={memoizedEmployment20}
            employment18={memoizedEmployment18}
            employment17={memoizedEmployment17}
            applicationInfo={memoizedApplicationInfo}
            progress={progress} companyId={companyId} year={year}
            changeData={changeData} navigation={navigation} setNavigation={setNavigation} setBackCaution={setBackCaution}
            setConfirm={setConfirm} />
        </div>

        {/* モバイル・タブレット用ナビゲーションバー */}
        <div className={Styles.mobile_nav_bar} onClick={() => setMobileNavOpen(true)}>
          <div className={Styles.mobile_nav_bar_left}>
            <p className={Styles.mobile_nav_step}>{navigation} / 11</p>
            <p className={Styles.mobile_nav_label}>
              {MOBILE_NAV_ITEMS.find(i => i.id === navigation)?.label}
            </p>
          </div>
          <VscChevronRight className={Styles.mobile_nav_chevron} />
        </div>

        <div className={Styles.center}>

          {navigation === 1 ?
          <Summary
            documentId={documentId}
            setChangeData={setChangeData}
            summary={memoizedSummary} setSummary={setSummary} />
          : navigation === 2 ?
          <Info
            companyId={companyId} year={year} setChangeData={setChangeData}
            info={memoizedInfo} setInfo={setInfo} />
          : navigation === 3 ?
          <Stores documentId={documentId} setStoreImageLocal={setStoreImageLocal} />
          : navigation === 4 ?
          <Target
            companyId={companyId} year={year} setChangeData={setChangeData}
            target={memoizedTarget} setTarget={setTarget} />
          : navigation === 5 ?
          <Employment
            graduateAge={20}
            companyId={companyId} year={year} setChangeData={setChangeData}
            info={info}
            employment17={memoizedEmployment17} setEmployment17={setEmployment17}
            employment18={memoizedEmployment18} setEmployment18={setEmployment18}
            employment20={memoizedEmployment20} setEmployment20={setEmployment20} />
          : navigation === 6 ?
          <Employment18
            companyId={companyId} year={year} setChangeData={setChangeData}
            info={info}
            employment17={memoizedEmployment17} setEmployment17={setEmployment17}
            employment18={memoizedEmployment18} setEmployment18={setEmployment18}
            employment20={memoizedEmployment20} setEmployment20={setEmployment20} />
          : navigation === 7 ?
          <Employment17
            companyId={companyId} year={year} setChangeData={setChangeData}
            info={info}
            employment17={memoizedEmployment17} setEmployment17={setEmployment17}
            employment18={memoizedEmployment18} setEmployment18={setEmployment18}
            employment20={memoizedEmployment20} setEmployment20={setEmployment20} />
          : navigation === 8 ?
          <Benefit
            info={memoizedInfo}
            companyId={companyId} year={year} setChangeData={setChangeData}
            benefit={memoizedBenefit} setBenefit={setBenefit}
            employment20={memoizedEmployment20} />
          : navigation === 9 ?
          <ApplicationInfo
            companyId={companyId} year={year} setChangeData={setChangeData}
            info={info}
            applicationInfo={memoizedApplicationInfo} setApplicationInfo={setApplicationInfo} />
          : navigation === 10 ?
          <Pamphlet
            documentId={documentId} setChangeData={setChangeData}
            pamphlet={memoizedPamphlet} setPamphlet={setPamphlet} />
          : navigation === 11 ?
          <HashTag
            companyId={companyId} year={year}
            setChangeData={setChangeData}
            hashTag={memoizedHashTag} setHashTag={setHashTag} />
          : <></>}

          <div style={{height: '30px'}}></div>

        </div>

        {/* デスクトップ用プレビュー（モバイルでは非表示） */}
        <div className={Styles.desktop_preview}>
          <Preview
            navigation={navigation}
            documentId={documentId}
            storeImageLocal={storeImageLocal}

            summary={memoizedSummary}
            info={memoizedInfo}
            target={memoizedTarget}
            benefit={memoizedBenefit}
            employment20={memoizedEmployment20}
            applicationInfo={memoizedApplicationInfo} />
        </div>
      </div>
      </>
    }
    </div>
    </div>

    {/* モバイル・タブレット用ナビゲーションモーダル */}
    {mobileNavOpen && (
      <div className={Styles.mobile_modal_overlay} onClick={() => setMobileNavOpen(false)}>
        <div className={Styles.mobile_modal} onClick={e => e.stopPropagation()}>
          <div className={Styles.mobile_modal_header}>
            <p className={Styles.mobile_modal_title}>求人票の編集</p>
            <button className={Styles.mobile_modal_close} onClick={() => setMobileNavOpen(false)}>✕</button>
          </div>

          {MOBILE_NAV_ITEMS.map(({ id, label, targetCondition }) => {
            const isActive = navigation === id;
            const isDisabled = targetCondition && !target?.target?.includes(targetCondition);

            return (
              <div
                key={id}
                className={Styles.mobile_modal_item}
                style={{
                  backgroundColor: isActive ? '#EBEBEB' : '',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.4 : 1,
                }}
                onClick={() => {
                  if (isDisabled) return;
                  if (changeData && navigation !== id) {
                    setBackCaution(id);
                  } else {
                    setNavigation(id);
                  }
                  setMobileNavOpen(false);
                }}
              >
                <p style={{ fontWeight: isActive ? 'bold' : '400' }}>
                  {id}. {label}
                </p>
                {progress[id - 1] ? (
                  <PiCheckCircleFill className={Styles.mobile_modal_save} />
                ) : (
                  <VscChevronRight className={Styles.mobile_modal_unsave} />
                )}
              </div>
            );
          })}

          <button
            className={Styles.mobile_modal_confirm_btn}
            onClick={handleMobilePublish}
            style={checkProgressMobile(progress, target.target)
              ? { cursor: 'pointer' }
              : { backgroundColor: '#fafafa', color: '#cccccc', border: '1px solid #EBEBEB', boxShadow: 'none', fontWeight: '400', cursor: 'default' }
            }
          >
            確定
          </button>
        </div>
      </div>
    )}

    {backCaution !== 0 && (
      <BackCaution backCaution={backCaution} setBackCaution={setBackCaution} setNavigation={setNavigation} setChangeData={setChangeData} resetToSaved={resetToSaved} />
    )}
    </>
  );
}

export default JobPostingEdit;
